import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getApps } from 'firebase/app';
import axios from 'axios';
import { API_URL } from './api';
import { getFirebaseAuth } from './firebase';
import { getStoredAuth } from '../contexts/CustomerAuthContext';

// Guard: only register one onMessage handler across all callers
let onMessageRegistered = false;

// VAPID key from build-time env or runtime config.json
let vapidKeyCache: string | null = null;
async function getVapidKey(): Promise<string> {
  if (vapidKeyCache) return vapidKeyCache;
  // Try build-time env first
  const fromEnv = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';
  if (fromEnv) { vapidKeyCache = fromEnv; return fromEnv; }
  // Fallback: runtime config.json
  try {
    const res = await fetch('/config.json');
    const cfg = await res.json();
    vapidKeyCache = (cfg?.VAPID_KEY as string) || '';
    return vapidKeyCache;
  } catch {
    return '';
  }
}

/**
 * Register FCM push for the current customer (no order required).
 * Requests permission, gets token, saves to Customer profile.
 */
export async function requestGlobalPushPermission(log?: (msg: string) => void): Promise<boolean> {
  const L = (msg: string) => { console.log(msg); log?.(msg); };
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      L('[push] SW or Notification API unavailable');
      return false;
    }

    const supported = await isSupported();
    if (!supported) return false;

    const vapidKey = await getVapidKey();
    if (!vapidKey) return false;

    // On iOS, calling requestPermission() outside a user gesture returns 'denied'
    // even if already granted. Only re-request if not yet granted.
    let permission = Notification.permission;
    if (permission !== 'granted') {
      permission = await Notification.requestPermission();
    }
    L(`[push] permission → ${permission}`);
    if (permission !== 'granted') return false;

    if (!getApps().length) await getFirebaseAuth();
    const apps = getApps();
    if (!apps.length) return false;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    const messaging = getMessaging(apps[0]);
    const fcmToken = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    if (!fcmToken) return false;

    // Save to Customer profile
    const storedAuth = getStoredAuth();
    if (storedAuth?.token) {
      await axios.post(
        `${API_URL || ''}/api/gifts/register-fcm`,
        { fcmToken },
        { headers: { Authorization: `Bearer ${storedAuth.token}` } },
      );
      L('[push] token saved to customer profile');
    }

    // Foreground handler: when PWA is open, do NOT show a system notification —
    // the user is already looking at the app and the system banner covers the notch.
    // Background pushes are still shown by the service worker (firebase-messaging-sw.js).
    if (!onMessageRegistered) {
      onMessageRegistered = true;
      onMessage(messaging, (payload) => {
        const tag = payload.data?.type || 'olso';
        console.log('[push] foreground message received (no banner):', tag);
      });
    }

    return true;
  } catch (err: any) {
    L(`[push] ${err?.message || String(err)}`);
    return false;
  }
}

export async function requestPushPermission(orderId: string, log?: (msg: string) => void): Promise<boolean> {
  const L = (msg: string) => { console.log(msg); log?.(msg); };
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      L('[push] ❌ SW or Notification API unavailable');
      return false;
    }

    L(`[push] permission=${Notification.permission}`);

    const supported = await isSupported();
    L(`[push] fcm supported=${supported}`);
    if (!supported) return false;

    const vapidKey = await getVapidKey();
    L(`[push] vapidKey=${vapidKey ? vapidKey.slice(0, 10) + '…' : '❌ MISSING'}`);
    if (!vapidKey) return false;

    // On iOS, calling requestPermission() outside a user gesture returns 'denied'
    // even if already granted. Only re-request if not yet granted.
    let permission = Notification.permission;
    if (permission !== 'granted') {
      permission = await Notification.requestPermission();
    }
    L(`[push] permission → ${permission}`);
    if (permission !== 'granted') return false;

    // Ensure Firebase app is initialized (it's lazy — may not exist yet)
    if (!getApps().length) {
      L('[push] init firebase app…');
      await getFirebaseAuth();
    }
    const apps = getApps();
    L(`[push] firebase apps=${apps.length}`);
    if (!apps.length) return false;

    L('[push] registering firebase-messaging-sw.js…');
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });
    await navigator.serviceWorker.ready;
    L(`[push] SW registered scope=${registration.scope}`);

    const messaging = getMessaging(apps[0]);
    L('[push] calling getToken…');
    const fcmToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    L(`[push] token=${fcmToken ? fcmToken.slice(0, 15) + '…' : '❌ EMPTY'}`);
    if (!fcmToken) return false;

    L('[push] posting token to backend…');
    await axios.post(`${API_URL || ''}/api/orders/${orderId}/fcm-token`, { fcmToken });
    L('[push] ✅ token saved to order');

    // Also save FCM token to Customer profile for gift notifications
    const storedAuth = getStoredAuth();
    if (storedAuth?.token) {
      try {
        await axios.post(
          `${API_URL || ''}/api/gifts/register-fcm`,
          { fcmToken },
          { headers: { Authorization: `Bearer ${storedAuth.token}` } },
        );
        L('[push] ✅ token saved to customer profile');
      } catch (err: any) {
        L(`[push] ⚠️ customer fcm save failed: ${err?.message}`);
      }
    } else {
      L('[push] ℹ️ no customer auth — skipping customer fcm save');
    }

    // Foreground handler: when app is open, FCM routes to onMessage (not SW).
    // Foreground handler: silent in PWA — system banner would cover the notch on iOS.
    // SW still handles background pushes via firebase-messaging-sw.js.
    if (!onMessageRegistered) {
      onMessageRegistered = true;
      onMessage(messaging, (payload) => {
        const tag = payload.data?.type || 'olso';
        L(`[push] foreground message received (no banner): ${tag}`);
      });
      L('[push] ✅ foreground onMessage handler registered (silent)');
    }

    return true;
  } catch (err: any) {
    L(`[push] ❌ ${err?.message || String(err)}`);
    return false;
  }
}
